import * as React from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppConfigsApi, useLlmGatewayApi } from "../../api";
import type { KeyTestResult, LlmCatalog, LlmProvider } from "../../models/domain";
import { errorMessage } from "../ui";
import { PROVIDER_KINDS, validKeyCheckPath, validProviderId } from "../settings/llmCatalog";
import { testVerdict } from "./providerText";

/* ------------------------------------------------------------------ fournisseur */

export interface ProviderEdit {
  /** Vide = ajout : l'identifiant se saisit. */
  id: string;
  provider: LlmProvider;
}

const EMPTY_PROVIDER: LlmProvider = {
  label: "",
  kind: "openai_compatible",
  base_url: "",
  enabled: true,
  student_data_allowed: false,
};

/** L'URL que la passerelle prend quand le champ est vide (types à URL publique). */
const PUBLIC_URLS: Record<Exclude<LlmProvider["kind"], "openai_compatible">, string> = {
  openai: "https://api.openai.com/v1",
  anthropic: "https://api.anthropic.com/v1",
};

// Le modèle d'OCR dédié (Mistral OCR) passe par la route OCR de la passerelle,
// qui l'appelle comme un fournisseur compatible OpenAI : c'est là qu'il se
// rattache, pas à un type à part.
const KIND_HINTS: Record<LlmProvider["kind"], string> = {
  openai: " ",
  anthropic: "API Messages d'Anthropic, appelée nativement par la passerelle.",
  openai_compatible:
    "Pour un modèle d'OCR dédié (Mistral OCR) : ce type, avec l'URL https://api.mistral.ai/v1.",
};

/**
 * Ajouter ou modifier un fournisseur : tout sauf la clé. L'identifiant ne
 * change plus une fois créé (il est dans chaque clé de modèle, dans les
 * tarifs et dans les appels tracés).
 */
export const ProviderDialog: React.FC<{
  edit: ProviderEdit | null;
  existing: string[];
  onClose: () => void;
  onSaved: (catalog: LlmCatalog) => void;
}> = ({ edit, existing, onClose, onSaved }) => {
  const { putLlmProvider } = useAppConfigsApi();
  const [id, setId] = React.useState("");
  const [p, setP] = React.useState<LlmProvider>(EMPTY_PROVIDER);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const adding = edit?.id === "";

  React.useEffect(() => {
    if (!edit) return;
    setId(edit.id);
    setP({ ...EMPTY_PROVIDER, ...edit.provider });
    setError(null);
  }, [edit]);

  const urlChanged = !adding && (edit?.provider.base_url ?? "") !== (p.base_url ?? "").trim();
  const idError = adding && id !== "" && (!validProviderId(id) || existing.includes(id));
  const keyPath = (p.key_check_path ?? "").trim();
  const keyPathError = keyPath !== "" && !validKeyCheckPath(keyPath);
  const valid =
    validProviderId(id) &&
    !idError &&
    p.label.trim() !== "" &&
    (p.kind !== "openai_compatible" || (p.base_url ?? "").trim().startsWith("https://")) &&
    !keyPathError;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await putLlmProvider(id, {
        ...p,
        base_url: (p.base_url ?? "").trim() || undefined,
        key_check_path: keyPath || undefined,
      });
      onSaved(data);
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Enregistrement refusé."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(edit)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{adding ? "Ajouter un fournisseur" : `Modifier ${edit?.provider.label ?? ""}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="Identifiant"
              value={id}
              disabled={!adding}
              onChange={(e) => setId(e.target.value)}
              error={idError}
              helperText={
                idError
                  ? existing.includes(id)
                    ? "Déjà utilisé."
                    : "Minuscules, chiffres, - et _ (pas en premier)."
                  : "Définitif : il préfixe les modèles (deepseek/deepseek-chat)."
              }
            />
            <TextField label="Libellé" value={p.label} onChange={(e) => setP({ ...p, label: e.target.value })} fullWidth />
          </Stack>
          <TextField
            select
            label="Type"
            value={p.kind}
            onChange={(e) => setP({ ...p, kind: e.target.value as LlmProvider["kind"] })}
            helperText={KIND_HINTS[p.kind]}
          >
            {PROVIDER_KINDS.map((k) => (
              <MenuItem key={k.kind} value={k.kind}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="URL de base"
            value={p.base_url ?? ""}
            onChange={(e) => setP({ ...p, base_url: e.target.value })}
            placeholder={p.kind === "openai_compatible" ? "https://api.exemple.com/v1" : PUBLIC_URLS[p.kind]}
            helperText={
              p.kind === "openai_compatible"
                ? "Obligatoire, en https."
                : `Vide = l'URL publique du fournisseur (${PUBLIC_URLS[p.kind]}).`
            }
          />
          <TextField
            label="Chemin du test de clé"
            value={p.key_check_path ?? ""}
            onChange={(e) => setP({ ...p, key_check_path: e.target.value })}
            placeholder="/models"
            error={keyPathError}
            helperText={
              keyPathError
                ? "Un chemin seul : /minuscules (chiffres, - _ . /), sans .. ni paramètres."
                : "Vide = /models. OpenRouter : /key (son /models est public et accepterait n'importe quelle clé)."
            }
          />
          {urlChanged && (
            <Alert severity="warning">
              La clé enregistrée est liée à l'ancienne URL : elle ne servira plus. Il faudra la saisir à nouveau.
            </Alert>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={p.student_data_allowed}
                onChange={(e) => setP({ ...p, student_data_allowed: e.target.checked })}
              />
            }
            label={
              <Typography variant="body2">
                Autorisé à recevoir des messages d'élèves (accord de traitement des données signé) — exigé pour le
                compagnon
              </Typography>
            }
          />
          <FormControlLabel
            control={<Checkbox checked={p.enabled} onChange={(e) => setP({ ...p, enabled: e.target.checked })} />}
            label={<Typography variant="body2">Actif : proposé dans les Réglages Tilmid+</Typography>}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={() => void submit()}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ clé */

/**
 * « Changer la clé » : la clé est saisie dans un champ mot de passe et part
 * vers la passerelle seule. Elle vit dans ce champ le temps de la saisie, puis
 * l'état est vidé à la fermeture ; rien ne la journalise. La passerelle la
 * teste avant de l'enregistrer : une clé refusée n'est pas écrite.
 */
export const KeyDialog: React.FC<{
  provider: { id: string; label: string } | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ provider, onClose, onSaved }) => {
  const { putProviderKey, testProviderKey } = useLlmGatewayApi();
  const [key, setKey] = React.useState("");
  const [busy, setBusy] = React.useState<"test" | "save" | null>(null);
  const [result, setResult] = React.useState<KeyTestResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const close = () => {
    setKey("");
    setResult(null);
    setError(null);
    onClose();
  };

  const run = async (what: "test" | "save") => {
    if (!provider) return;
    setBusy(what);
    setError(null);
    setResult(null);
    try {
      if (what === "test") {
        const { data } = await testProviderKey(provider.id, key.trim());
        setResult(data.test);
      } else {
        await putProviderKey(provider.id, key.trim());
        onSaved();
        close();
      }
    } catch (e) {
      const data = (e as { response?: { status?: number; data?: { test?: KeyTestResult } } }).response;
      if (data?.data?.test) setResult(data.data.test);
      setError(
        data?.status === 404
          ? "La passerelle ne connaît pas encore ce fournisseur : elle relit les fournisseurs régulièrement, réessayez dans un instant."
          : errorMessage(e, "La passerelle a refusé la clé."),
      );
    } finally {
      setBusy(null);
    }
  };

  const verdict = result ? testVerdict(result) : null;
  return (
    <Dialog open={Boolean(provider)} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Changer la clé — {provider?.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            La clé part directement à la passerelle, qui la chiffre ; elle n'est ni affichée ni gardée ici. Elle est testée
            avant d'être enregistrée. L'ancienne clé sert jusqu'à l'enregistrement : pensez ensuite à la révoquer chez le
            fournisseur.
          </Typography>
          <TextField
            label="Nouvelle clé"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            autoComplete="new-password"
            inputProps={{ spellCheck: false, "aria-label": "Nouvelle clé" }}
            fullWidth
          />
          {verdict && <Alert severity={verdict.ok ? "success" : "error"}>{verdict.text}</Alert>}
          {error && !verdict && <Alert severity="error">{error}</Alert>}
          {error && verdict && !verdict.ok && <Typography variant="caption">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Annuler</Button>
        <Button disabled={!key.trim() || busy !== null} onClick={() => void run("test")}>
          {busy === "test" ? "Test…" : "Tester cette clé"}
        </Button>
        <Button variant="contained" disabled={!key.trim() || busy !== null} onClick={() => void run("save")}>
          {busy === "save" ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
